/* Native POST and Shopify spam protection remain in charge of submission.
 * Analytics carry no email, email hash, full URL, customer ID or free-form text.
 * Accepted != new subscribed customer, confirmed consent, or delivered email.
 */
(() => {
  if (customElements.get('sx-newsletter')) return;
  const pendingKey = 'sx-newsletter:pending';
  const acceptedKey = 'sx-newsletter:accepted';
  const maxAge = 30 * 60 * 1000;
  const storage = {
    get(key) { try { return JSON.parse(sessionStorage.getItem(key)); } catch (_) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; } },
    remove(key) { try { sessionStorage.removeItem(key); } catch (_) { /* Functional form works without storage. */ } }
  };
  function publish(element, name, extra = {}) {
    if (element.dataset.analytics !== 'true') return false;
    const privacy = window.Shopify?.customerPrivacy;
    const analytics = window.Shopify?.analytics;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function' || !privacy.analyticsProcessingAllowed()) return false;
    if (typeof analytics?.publish !== 'function') return false;
    try {
      Promise.resolve(analytics.publish(`sx_newsletter_${name}`, {
        placement: element.dataset.placement,
        form_id: element.form.id,
        ...extra
      })).catch(() => {});
      return true;
    } catch (_) { return false; }
  }
  class SXNewsletter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.initialized = true;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.status = this.querySelector('[data-state]');
      this.input = this.querySelector('input[type=email]');
      this.button = this.querySelector('button[type=submit]');
      this.releaseSubmit = () => {
        clearTimeout(this.submissionTimer);
        this.submitting = false;
        this.button.removeAttribute('aria-disabled');
      };
      // Block rapid repeated user activation, not submit events. Shopify can
      // immediately resubmit the form after its spam challenge completes.
      this.button.addEventListener('click', (event) => {
        if (this.submitting) event.preventDefault();
      }, { signal });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && this.submitting) event.preventDefault();
      }, { signal });
      this.invalidAttempt = false;
      this.form.addEventListener('invalid', () => {
        if (this.invalidAttempt) return;
        this.invalidAttempt = true;
        publish(this, 'attempt');
        publish(this, 'error', { reason: 'validation' });
        this.input.setAttribute('aria-invalid', 'true');
        this.showError('Enter a valid email address, such as name@example.com.');
        // Native validation focuses the input and prevents any network request.
        setTimeout(() => { this.invalidAttempt = false; }, 0);
      }, { capture: true, signal });
      this.input.addEventListener('input', () => {
        this.releaseSubmit();
        this.input.removeAttribute('aria-invalid');
        if (this.status.dataset.state === 'error') {
          this.status.textContent = '';
          this.status.dataset.state = 'idle';
        }
      }, { signal });
      this.form.addEventListener('submit', (event) => {
        // Never cancel a native submit, including a challenge resubmission.
        if (this.submitting) return;
        this.submitting = true;
        this.button.setAttribute('aria-disabled', 'true');
        publish(this, 'attempt');
        storage.set(pendingKey, { form: this.form.id, placement: this.dataset.placement, at: Date.now() });
        // Do not disable fields, replace the POST, or intercept Shopify's challenge.
        // Unlock if Shopify prevents navigation for a challenge/recoverable error.
        this.submissionTimer = setTimeout(this.releaseSubmit, 2500);
      }, { signal });
      this.onPageShow = this.releaseSubmit;
      window.addEventListener('pageshow', this.onPageShow);
      if (this.dataset.analytics !== 'true' || window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures) this.resolveResponse();
      this.onConsent = () => {
        if (this.visible && !this.viewed) this.viewed = publish(this, 'view');
      };
      document.addEventListener('visitorConsentCollected', this.onConsent);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
          this.onConsent();
        }, { threshold: 0.5 });
        this.observer.observe(this);
      }
      if (this.dataset.analytics === 'true' && !window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
          if (!error) this.onConsent();
          this.resolveResponse();
        });
      }
    }
    resolveResponse() {
      const pending = storage.get(pendingKey);
      const matching = pending && pending.form === this.form.id && pending.placement === this.dataset.placement;
      const fresh = matching && Date.now() - pending.at >= 0 && Date.now() - pending.at < maxAge;
      const state = this.status.dataset.state;
      const anchorMatches = window.location.hash === `#${this.form.id}`;
      if (state === 'idle') return;
      // Shopify's customer-form response can appear on every customer form.
      // Only the initiating placement receives enhancement/focus/measurement.
      if (!anchorMatches && !fresh) return;
      this.status.focus({ preventScroll: true });
      if (!fresh) return;
      storage.remove(pendingKey); // Consume once, before publishing, including on refresh.
      if (state === 'error') {
        publish(this, 'error', { reason: 'server' });
      } else if (state === 'accepted' && !storage.get(acceptedKey)) {
        // No identity is persisted. Conservative session-wide deduplication also
        // suppresses repeat acceptance across placements. No storage: no conversion.
        if (storage.set(acceptedKey, true)) publish(this, 'accepted', { verification: 'form_response_only' });
      }
    }
    showError(message) {
      this.status.dataset.state = 'error';
      const paragraph = document.createElement('p');
      paragraph.textContent = message;
      this.status.replaceChildren(paragraph);
    }
    disconnectedCallback() {
      this.releaseSubmit?.();
      this.listeners?.abort();
      this.observer?.disconnect();
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      window.removeEventListener('pageshow', this.onPageShow);
      // Shopify editor reattachment needs observation/listeners restored.
      this.initialized = false;
    }
  }
  customElements.define('sx-newsletter', SXNewsletter);
})();
